/**
 * Admin/Hierarchy Types
 * TypeScript type definitions for tenant hierarchy and admin operations
 * Used across admin, group_admin, and chatbots modules
 */

// Maximum tenant hierarchy depth (must match backend MAX_TENANT_DEPTH)
export const MAX_TENANT_DEPTH = 5;

// Tenant with hierarchy info
export interface TenantHierarchy {
  tenant_id: string;
  tenant_name: string;
  tenant_code: string;
  parent_tenant_id: string | null;
  is_root_tenant: boolean;
  subscription_plan: string;
  tenant_status: string;
  depth?: number;  // Hierarchy depth (0 = root, 1 = first child level, etc.)
  child_tenant_count?: number;
  has_children?: boolean;  // True if tenant has child tenants (can be parent for new tenants)
  parent_tenant_name?: string;
  user_count?: number;
  total_users?: number;
  // Pre-auth host routing. tenant_domain matches the request Host exactly
  // (e.g. "app.acme.com"); tenant_subdomain matches the leftmost label
  // (e.g. "acme" → "acme.corpbrain.com"). Both unique across tenants.
  // `subdomain_url` is the backend-computed default URL —
  // "https://{tenant_subdomain}.corpbrain.com" — or null if no subdomain.
  tenant_domain?: string | null;
  tenant_subdomain?: string | null;
  subdomain_url?: string | null;
  is_company?: boolean;
  // Per-tenant `auth.transactional_email` flag. When false, no
  // password-reset / invitation EMAILS are available for this tenant
  // (admins can still create users and set passwords directly).
  transactional_email?: boolean;
  created_at: string;
  updated_at: string;
}

// User in a tenant
export interface TenantUser {
  user_id: string;
  tenant_id: string;
  tenant_name?: string;
  tenant_has_children?: boolean;  // True if user's tenant has child tenants (can have group_admin)
  tenant_is_root?: boolean;  // True if user's tenant is the root tenant (can have super_admin)
  email: string;
  display_name: string;
  role: 'user' | 'admin' | 'group_admin' | 'super_admin';
  is_active: boolean;
  /** False = invited/pending — the user hasn't set a password yet. */
  has_password?: boolean;
  /**
   * The user's own tenant `auth.transactional_email` flag. Present on
   * group_admin / super_admin user-list rows; absent on admin-role rows
   * (those fall back to the logged-in admin's own-tenant feature flag).
   */
  transactional_email?: boolean;
  user_tier?: number;
  created_at: string;
  last_login: string | null;
  total_conversations?: number;
  total_messages?: number;
  active_days?: number;
  total_tokens?: number;
  total_cost_usd?: number;
}

// Create tenant payload
export interface CreateTenantPayload {
  tenant_name: string;
  tenant_code: string;
  parent_tenant_id?: string;
  subscription_plan?: string;
  initial_admin?: {
    email: string;
    password: string;
    display_name?: string;
  };
}

// Update tenant payload
export interface UpdateTenantPayload {
  tenant_name?: string;
  tenant_status?: string;
  tenant_settings?: Record<string, unknown>;
  tenant_domain?: string | null;
  tenant_subdomain?: string | null;
}

// Create user payload
export interface CreateUserPayload {
  email: string;
  password: string;
  role: 'user' | 'admin' | 'group_admin' | 'super_admin';
  display_name?: string;
}

// Update user payload
export interface UpdateUserPayload {
  role?: 'user' | 'admin' | 'group_admin' | 'super_admin';
  display_name?: string;
  is_active?: boolean;
  user_tier?: number;
}

// Hierarchy tree node
export interface HierarchyTreeNode {
  tenant_id: string;
  tenant_name: string;
  tenant_code: string;
  is_root_tenant: boolean;
  has_children?: boolean;  // True if tenant has child tenants (can have group_admin users)
  user_count?: number;
  total_users?: number;
  total_users_with_descendants?: number;
  children: HierarchyTreeNode[];
}

// API Responses
export interface TenantsListResponse {
  success: boolean;
  tenants: TenantHierarchy[];
  total: number;
}

export interface TenantResponse {
  success: boolean;
  tenant: TenantHierarchy;
}

export interface UsersListResponse {
  success: boolean;
  users: TenantUser[];
  total: number;
}

export interface UserResponse {
  success: boolean;
  user: TenantUser;
}

export interface HierarchyTreeResponse {
  success: boolean;
  hierarchy: HierarchyTreeNode;
}

export interface ValidateChildResponse {
  success: boolean;
  can_create_child: boolean;
  error: string;
}

// Bot Sharing Types
export interface ChatbotShare {
  share_id: string;
  chatbot_id: string;
  shared_by_user_id: string;
  shared_by_user_email?: string;
  permission_level: 'view' | 'chat' | 'edit';
  can_reshare: boolean;
  shared_at: string;
  share_type: 'user' | 'tenant';
  // User share specific
  shared_with_user_id?: string;
  shared_with_user_email?: string;
  shared_with_user_name?: string;
  // Tenant share specific
  shared_with_tenant_id?: string;
  shared_with_tenant_name?: string;
}

export interface ShareBotPayload {
  target_user_id?: string;
  target_tenant_id?: string;
  permission_level?: 'view' | 'chat' | 'edit';
  can_reshare?: boolean;
}

export interface BotSharesListResponse {
  success: boolean;
  shares: ChatbotShare[];
  total: number;
}

export interface BotShareResponse {
  success: boolean;
  share: ChatbotShare;
}

// Share Targets - users and tenants that can be shared with
export interface ShareableUser {
  user_id: string;
  user_email: string;
  user_full_name: string | null;
  user_role: string;
  tenant_id: string;
  tenant_name: string | null;
}

export interface ShareableTenant {
  tenant_id: string;
  tenant_name: string;
  tenant_code: string;
  is_root_tenant: boolean;
  depth: number;
  parent_tenant_id: string | null;
  hierarchy_path: string;
}

export interface ShareTargetsResponse {
  success: boolean;
  users: ShareableUser[];
  tenants: ShareableTenant[];
}

// Bot Analytics Types
export interface BotShareTenantDetail {
  tenant_id: string;
  tenant_name: string;
  can_reshare: boolean;
  permission_level: string;
}

export interface BotShareUserDetail {
  user_id: string;
  email: string;
  name: string | null;
  tenant_name: string;
  permission_level: string;
}

export interface BotShareDetails {
  tenants: BotShareTenantDetail[];
  users: BotShareUserDetail[];
}

export interface BotAnalytics {
  chatbot_id: string;
  chatbot_name: string;
  chatbot_description: string | null;
  creator_email: string;
  creator_name: string | null;
  created_at: string | null;
  is_active: boolean;
  shared_tenant_count: number;
  shared_user_count: number;
  share_details: BotShareDetails;
  total_conversations: number;
  total_messages: number;
  total_tokens: number;
  total_cost_usd: number;
  unique_users: number;
}

export interface BotAnalyticsListResponse {
  success: boolean;
  bots: BotAnalytics[];
  total: number;
}

// Generic Entity Sharing Types (for data bundles and skills)
export interface EntityShare {
  share_id: string;
  shared_by_user_id: string;
  shared_by_user_email?: string;
  permission_level: 'use';
  can_reshare: boolean;
  shared_at: string;
  share_type: 'user' | 'tenant';
  shared_with_user_id?: string;
  shared_with_user_email?: string;
  shared_with_user_name?: string;
  shared_with_tenant_id?: string;
  shared_with_tenant_name?: string;
  visible_to_roles?: string[] | null;
  // Entity-specific IDs (one will be present)
  data_bundle_id?: string;
  skill_id?: string;
}

export interface ShareEntityPayload {
  target_user_id?: string;
  target_tenant_id?: string;
  permission_level?: 'use';
  can_reshare?: boolean;
  visible_to_roles?: string[] | null;
}

export interface EntitySharesListResponse {
  success: boolean;
  shares: EntityShare[];
  total: number;
}

export interface EntityShareResponse {
  success: boolean;
  share: EntityShare;
}

export interface BulkSharePayload {
  target_tenant_ids: string[];
  permission_level?: 'use';
  can_reshare?: boolean;
  visible_to_roles?: string[] | null;
}

export interface BulkShareResult {
  success: boolean;
  created: Array<{ tenant_id: string; share_id: string }>;
  failed: Array<{ tenant_id: string; error: string }>;
  skipped: Array<{ tenant_id: string; reason: string }>;
}

export interface BulkShareUsersPayload {
  target_user_ids: string[];
  permission_level?: 'use';
  can_reshare?: boolean;
}

export interface BulkShareUsersResult {
  success: boolean;
  created: Array<{ user_id: string; share_id: string }>;
  failed: Array<{ user_id: string; error: string }>;
  skipped: Array<{ user_id: string; reason: string }>;
}
